/**
 * <AccountPickerGroup> — les comptes d'une famille sur « Qui utilise Masteur ? » (organism).
 *
 * Dans l'ordre : les comptes élèves, la tuile « Espace parent » dès que le groupe a un élève
 * (on entre dans l'espace parent par le compte d'un enfant), puis les autres comptes. Les listes
 * arrivent triées — le plus récemment utilisé en premier, c'est le view-model qui trie.
 *
 * Deux sortes de groupes :
 *  - une **famille** : son nom sous ses tuiles (pas quand elle est seule sur l'écran, `alone`) ;
 *  - les comptes **hors famille** (un tuteur, un admin, un élève seul) : pas de nom.
 *
 * En mode de retrait (`managing`), ce qui quitte l'appareil se retire en une fois (Wajih, 21/10) :
 *  - un compte hors famille porte la marque du retrait, et son appui demande SON retrait ;
 *  - une famille se retire entière, par son seul « Retirer la famille », sous elle — ses comptes
 *    partent ensemble, comme dans l'ancien écran. Ses enfants ne portent pas de marque et ne
 *    répondent plus : un enfant ne se retire jamais seul.
 * Un geste absent (`onRemoveFamily`, `onRemoveAccount`) retire la marque ou le mot. « Espace
 * parent » n'est pas un compte : il ne porte pas de marque et ne répond plus.
 *
 * Les formes `UIStoredAccount*` vivent dans `ports/primary/ui/types/storedAccount`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { AccountPickerTile } from "../../molecules/AccountPickerTile"
import {
    type UIAccountPickerTarget,
    type UIStoredAccount,
    type UIStoredAccountFamily,
    type UIStoredAccountGroup,
} from "../../../ports/primary/ui/types"

export type AccountPickerGroupProps = {
    group: UIStoredAccountGroup
    /** Le seul groupe de l'écran : une famille n'écrit pas son nom. */
    alone?: boolean
    /**
     * Ce qui s'ouvre, sur TOUT l'écran : la tuile visée souligne son prénom en boucle, les autres ne
     * répondent plus — y compris celles d'un autre groupe.
     */
    opening?: UIAccountPickerTarget | null
    /**
     * L'écran est en mode de retrait : les tuiles ne s'ouvrent plus, un compte hors famille porte la
     * marque du retrait, une famille porte « Retirer la famille ».
     */
    managing?: boolean
    onOpenAccount?: (account: UIStoredAccount) => void
    onOpenParentSpace?: (group: UIStoredAccountGroup) => void
    /** L'appui d'un compte hors famille, en mode de retrait. */
    onRemoveAccount?: (account: UIStoredAccount) => void
    /** « Retirer la famille », en mode de retrait. */
    onRemoveFamily?: (family: UIStoredAccountFamily) => void
    testID?: string
}

export function AccountPickerGroup(props: AccountPickerGroupProps) {
    const {
        group,
        alone = false,
        opening = null,
        managing = false,
        onOpenAccount,
        onOpenParentSpace,
        onRemoveAccount,
        onRemoveFamily,
        testID,
    } = props

    const inert = opening != null || managing
    // Le nom d'une famille s'écrit sous ses tuiles, sauf quand elle est seule sur l'écran.
    const showName = group.kind === "family" && !alone
    const openingParentSpace = opening?.kind === "parentSpace" && opening.groupId === group.id

    /** En mode de retrait, seul un compte hors famille se retire par sa tuile : une famille part entière. */
    const removable = managing && group.kind === "individual" && onRemoveAccount != null

    const accountTile = (account: UIStoredAccount) => {
        const loading = opening?.kind === "account" && opening.accountId === account.id
        return (
            <AccountPickerTile
                key={account.id}
                label={account.firstname}
                avatarUri={account.avatarUrl}
                tutor={account.isTutor}
                loading={loading}
                // En mode de retrait, la tuile marquée répond (elle demande le retrait) ; les autres non.
                disabled={opening != null || (managing && !removable)}
                onPress={() => onOpenAccount?.(account)}
                onRemove={removable ? () => onRemoveAccount?.(account) : undefined}
            />
        )
    }

    const removeFamily =
        managing && group.kind === "family" && onRemoveFamily != null ? (
            <TextButton
                label="Retirer la famille"
                accessibilityLabel={`Retirer ${group.name}`}
                color="danger"
                onPress={() => onRemoveFamily(group)}
            />
        ) : null

    return (
        <View testID={testID} style={styles.group}>
            <View style={styles.tiles}>
                {group.students.map(accountTile)}
                {group.parentSpaceAccountId != null && (
                    <AccountPickerTile
                        variant="parentSpace"
                        loading={openingParentSpace}
                        disabled={inert}
                        onPress={() => onOpenParentSpace?.(group)}
                    />
                )}
                {group.others.map(accountTile)}
            </View>
            {showName || removeFamily != null ? (
                <Stack gap="1" align="center">
                    {showName && <SectionLabel center>{group.name}</SectionLabel>}
                    {removeFamily}
                </Stack>
            ) : null}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    group: {
        alignSelf: "stretch",
        gap: theme.spacing["4"],
    },
    tiles: {
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "flex-start",
        columnGap: { xs: theme.spacing["3"], md: theme.spacing["5"] },
        rowGap: theme.spacing["6"],
    },
}))
